import { useState, useCallback } from 'react';
import { apiFetch } from '../api';

function ReceiptExporter() {
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-01-01`;
  });
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    return d.toISOString().split('T')[0];
  });
  const [generating, setGenerating] = useState(false);
  const [progressMsg, setProgressMsg] = useState('');
  const [previewHtml, setPreviewHtml] = useState(null);

  const formatCurrency = useCallback((amount) => {
    if (amount == null) return '$0.00';
    return '$' + Number(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }, []);

  async function fetchAttachmentDataUrl(receiptId, filename) {
    try {
      const res = await apiFetch(
        `/api/receipt-records/${receiptId}/attachment-base64?filename=${encodeURIComponent(filename)}`
      );
      if (res.ok) {
        const data = await res.json();
        return `data:${data.contentType};base64,${data.data}`;
      }
    } catch {}
    return null;
  }

  function isImage(filename) {
    return /\.(png|jpg|jpeg|gif|webp)$/i.test(filename);
  }

  async function generateExport() {
    setGenerating(true);
    setProgressMsg('Loading receipts...');
    setPreviewHtml(null);

    try {
      const res = await apiFetch('/api/receipt-records');
      if (!res.ok) throw new Error('Failed to load receipts');
      const allReceipts = await res.json();

      // Filter by date range and finalized only
      const filtered = allReceipts.filter((r) => {
        if (!r.isFinal) return false;
        if (!r.date) return false;
        return r.date >= startDate && r.date <= endDate;
      }).sort((a, b) => a.date.localeCompare(b.date));

      if (filtered.length === 0) {
        setProgressMsg('No finalized receipts found in this date range.');
        setGenerating(false);
        return;
      }

      // Group by category
      const byCategory = {};
      let grandTotal = 0;
      for (const r of filtered) {
        const cat = r.category || 'Uncategorized';
        if (!byCategory[cat]) byCategory[cat] = { receipts: [], total: 0 };
        byCategory[cat].receipts.push(r);
        byCategory[cat].total += r.amount || 0;
        grandTotal += r.amount || 0;
      }

      // Fetch attachment data for embedding
      setProgressMsg(`Loading attachments for ${filtered.length} receipts...`);
      const attachmentCache = {};
      let loaded = 0;
      for (const r of filtered) {
        if (r.attachmentNames && r.attachmentNames.length > 0) {
          attachmentCache[r.id] = [];
          for (const fn of r.attachmentNames) {
            if (isImage(fn)) {
              const dataUrl = await fetchAttachmentDataUrl(r.id, fn);
              if (dataUrl) attachmentCache[r.id].push({ filename: fn, dataUrl, type: 'image' });
            } else {
              attachmentCache[r.id].push({ filename: fn, dataUrl: null, type: 'file' });
            }
          }
        }
        loaded++;
        if (loaded % 5 === 0) {
          setProgressMsg(`Loading attachments... ${loaded}/${filtered.length}`);
        }
      }

      // Build HTML document
      setProgressMsg('Building PDF...');
      const categories = Object.keys(byCategory).sort();

      let html = `
        <style>
          body { font-family: Arial, sans-serif; color: #222; margin: 0; padding: 20px; }
          h1 { font-size: 22px; margin-bottom: 4px; }
          h2 { font-size: 16px; margin: 20px 0 8px; border-bottom: 2px solid #333; padding-bottom: 4px; }
          h3 { font-size: 14px; margin: 16px 0 6px; color: #444; }
          .subtitle { font-size: 13px; color: #666; margin-bottom: 16px; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 12px; font-size: 12px; }
          th { background: #f0f0f0; text-align: left; padding: 6px 8px; border: 1px solid #ccc; font-weight: 600; }
          td { padding: 6px 8px; border: 1px solid #ddd; }
          .amount { text-align: right; font-family: monospace; }
          .total-row td { font-weight: 700; background: #f8f8f8; border-top: 2px solid #333; }
          .grand-total td { font-weight: 700; background: #e8e8e8; border-top: 3px solid #000; font-size: 14px; }
          .receipt-detail { page-break-inside: avoid; margin: 12px 0; padding: 12px; border: 1px solid #ddd; border-radius: 4px; }
          .receipt-detail-header { display: flex; justify-content: space-between; margin-bottom: 8px; }
          .receipt-detail-header strong { font-size: 14px; }
          .receipt-detail-meta { font-size: 11px; color: #666; margin-bottom: 8px; }
          .receipt-detail-meta span { margin-right: 16px; }
          .receipt-comments { font-size: 12px; color: #555; font-style: italic; margin-top: 6px; padding: 6px; background: #fafafa; border-radius: 3px; }
          .attachment-img { max-width: 100%; max-height: 600px; margin: 8px 0; border: 1px solid #ddd; }
          .attachment-note { font-size: 11px; color: #888; margin: 4px 0; }
          .page-break { page-break-before: always; }
        </style>
        <h1>Expense Report</h1>
        <div class="subtitle">${startDate} to ${endDate} &mdash; ${filtered.length} receipt${filtered.length !== 1 ? 's' : ''}</div>

        <h2>Summary by Category</h2>
        <table>
          <thead>
            <tr><th>Category</th><th>Count</th><th class="amount">Total</th></tr>
          </thead>
          <tbody>
      `;

      for (const cat of categories) {
        html += `<tr>
          <td>${cat}</td>
          <td>${byCategory[cat].receipts.length}</td>
          <td class="amount">${formatCurrency(byCategory[cat].total)}</td>
        </tr>`;
      }

      html += `
          <tr class="grand-total">
            <td>Grand Total</td>
            <td>${filtered.length}</td>
            <td class="amount">${formatCurrency(grandTotal)}</td>
          </tr>
          </tbody>
        </table>

        <h2>All Receipts</h2>
        <table>
          <thead>
            <tr><th>Date</th><th>Vendor</th><th>Category</th><th>Description</th><th class="amount">Amount</th></tr>
          </thead>
          <tbody>
      `;

      for (const r of filtered) {
        html += `<tr>
          <td>${r.date}</td>
          <td>${r.vendor || ''}</td>
          <td>${r.category || ''}</td>
          <td>${r.description || ''}</td>
          <td class="amount">${formatCurrency(r.amount)}</td>
        </tr>`;
      }

      html += `
          <tr class="grand-total">
            <td colspan="4">Total</td>
            <td class="amount">${formatCurrency(grandTotal)}</td>
          </tr>
          </tbody>
        </table>
      `;

      // Detail pages
      html += '<div class="page-break"></div>';
      html += '<h2>Receipt Details</h2>';

      for (let i = 0; i < filtered.length; i++) {
        const r = filtered[i];
        html += `
          <div class="receipt-detail">
            <div class="receipt-detail-header">
              <strong>${r.vendor || 'Unknown Vendor'}</strong>
              <strong>${formatCurrency(r.amount)}</strong>
            </div>
            <div class="receipt-detail-meta">
              <span>Date: ${r.date}</span>
              <span>Category: ${r.category || 'N/A'}</span>
              ${r.source === 'email' ? `<span>Source: Email (${r.sourceAccount || ''})</span>` : '<span>Source: Manual</span>'}
            </div>
            ${r.description ? `<div style="font-size:12px;margin-bottom:6px;">${r.description}</div>` : ''}
            ${r.comments ? `<div class="receipt-comments">${r.comments}</div>` : ''}
        `;

        // Attachments
        const attachments = attachmentCache[r.id];
        if (attachments && attachments.length > 0) {
          html += '<h3>Attachments</h3>';
          for (const att of attachments) {
            if (att.type === 'image' && att.dataUrl) {
              html += `<div class="attachment-note">${att.filename}</div>`;
              html += `<img class="attachment-img" src="${att.dataUrl}" />`;
            } else {
              html += `<div class="attachment-note">${att.filename} (${att.type === 'file' ? 'PDF/file — not embeddable in export' : 'could not load'})</div>`;
            }
          }
        }

        html += '</div>';
      }

      setPreviewHtml(html);
      setProgressMsg('');
    } catch (err) {
      setProgressMsg(`Error: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  }

  async function downloadPdf() {
    if (!previewHtml) return;
    setGenerating(true);
    setProgressMsg('Generating PDF...');

    try {
      const container = document.createElement('div');
      container.style.position = 'fixed';
      container.style.top = '0';
      container.style.left = '0';
      container.style.width = '800px';
      container.style.zIndex = '-1';
      container.style.background = '#fff';
      container.style.color = '#000';
      container.style.padding = '20px';
      container.style.overflow = 'visible';
      container.innerHTML = previewHtml;
      document.body.appendChild(container);

      const images = container.querySelectorAll('img');
      if (images.length > 0) {
        await Promise.allSettled(
          Array.from(images).map(
            (img) => new Promise((resolve) => {
              if (img.complete) return resolve();
              img.onload = resolve;
              img.onerror = resolve;
            })
          )
        );
      }
      await new Promise((r) => setTimeout(r, 500));

      const { default: html2pdf } = await import('html2pdf.js');
      const pdfBlob = await html2pdf()
        .set({
          margin: [10, 10, 10, 10],
          filename: `expense-report-${startDate}-to-${endDate}.pdf`,
          image: { type: 'jpeg', quality: 0.95 },
          html2canvas: { scale: 2, useCORS: true, allowTaint: true, logging: false, windowWidth: 800 },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['css'], before: '.page-break' },
        })
        .from(container)
        .outputPdf('blob');

      document.body.removeChild(container);

      // Download
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `expense-report-${startDate}-to-${endDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setProgressMsg('PDF downloaded!');
    } catch (err) {
      setProgressMsg(`Error: ${err.message}`);
    } finally {
      setGenerating(false);
    }
  }

  return (
    <section className="card receipt-exporter-section">
      <h2>Receipt Exporter</h2>

      <div className="re-controls">
        <div className="re-date-range">
          <label className="re-label">
            From
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="re-date-input"
            />
          </label>
          <label className="re-label">
            To
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="re-date-input"
            />
          </label>
        </div>
        <div className="re-actions">
          <button
            className="manage-btn"
            onClick={generateExport}
            disabled={generating}
          >
            {generating ? 'Generating...' : 'Preview Export'}
          </button>
          {previewHtml && (
            <button
              className="manage-btn re-download-btn"
              onClick={downloadPdf}
              disabled={generating}
            >
              Download PDF
            </button>
          )}
        </div>
      </div>

      {progressMsg && (
        <div className="re-progress">{progressMsg}</div>
      )}

      {previewHtml && (
        <div className="re-preview">
          <iframe
            srcDoc={`<!DOCTYPE html><html><body>${previewHtml}</body></html>`}
            title="Export Preview"
            className="re-preview-iframe"
          />
        </div>
      )}
    </section>
  );
}

export default ReceiptExporter;
